import { StoreData } from "@builder/store/slices/BuilderSlice/types";
import { useGetCustomersQuery } from "@builder/store/slices/dataApiSlices/customersApiSlice";
import { useGetOrdersQuery } from "@builder/store/slices/dataApiSlices/ordersApiSlice";
import { useGetProductsQuery } from "@builder/store/slices/dataApiSlices/productsApiSlice";
import { useAppSelector } from "@builder/store/store";
import { ActiveEntityType } from "@common/build/activeEntityType";
import { QueryDataTables } from "@common/build/constants";
import { BuildNodeQuery, DataType } from "@common/build/types";
import HookWrapper from "@common/components/HookWrapper";
import { DynamicDataSectionProvider } from "@common/Contexts/DynamicDataTypeContext";
import { skipToken } from "@reduxjs/toolkit/query";
import { ComponentType } from "react";

export interface WrapperProps {
    component: ComponentType<any>;
    activeEntities?: any;
    [key: string]: any;
    singleStoreDataType?: keyof StoreData;
}

//this is to load redux store data that doesn't need refetching - yet is not dynamic data in functionality.
// For example if you wanna send a whole order data from redux etc
export const SingleStoreDataWrapper = (props: WrapperProps) => {
    const type = props.singleStoreDataType as keyof StoreData;
    const storeData = useAppSelector(state => state.storeData.data);

    if (!storeData) {
        return <HookWrapper {...props} />;
    }

    if (type === "order") {
        const orderData = storeData.order;
        return <HookWrapper {...props} order={props?.dataObject || orderData} />;
    }

    //in the future we can implement more single store data elements as needed
    return <HookWrapper {...props} />;
};

const DynamicGridWrapper = (props: WrapperProps) => {
    // const decimals = useAppSelector(state => state.builder.storeData?.company.s)
    // const [products, setProducts] = useState<ActiveProduct[]>([]);
    // const [orders, setOrders] = useState<ActiveOrder[]>([]);
    // const [customers, setCustomers] = useState<ActiveCustomer[]>([]);
    const storeData = useAppSelector(state => state.storeData);
    const companyId = storeData.data?.company?._id;
    const { isOrg, company } = storeData;
    const query: BuildNodeQuery<DataType> = props.node.query;

    const { data: customers } = useGetCustomersQuery(
        companyId && query.dataType === QueryDataTables.CUSTOMERS
            ? { companyId, limit: 10, ...(isOrg && { organizationId: company?._id }) }
            : skipToken
    );
    const { data: orders } = useGetOrdersQuery(
        companyId && query.dataType === QueryDataTables.ORDERS ? { companyId, limit: 10, ...(isOrg && { organizationId: company?._id }) } : skipToken
    );
    const { data: products } = useGetProductsQuery(
        companyId && query.dataType === QueryDataTables.PRODUCTS
            ? { companyId, limit: 10, ...(isOrg && { organizationId: company?._id }) }
            : skipToken
    );

    const getDynamicData = () => {
        switch (query.dataType) {
            case QueryDataTables.CUSTOMERS:
                return customers || [];
            case QueryDataTables.ORDERS:
                return orders || [];
            case QueryDataTables.PRODUCTS:
                return products || [];
            default:
                return [];
        }
    };

    return (
        <DynamicDataSectionProvider
            value={{
                dynamicDataScope:
                    query.dataType === QueryDataTables.CUSTOMERS
                        ? ActiveEntityType.CUSTOMER
                        : query.dataType === QueryDataTables.ORDERS
                        ? ActiveEntityType.ORDER
                        : ActiveEntityType.PRODUCT,
                dynamicDataObject: {}
            }}
        >
            <HookWrapper {...props} dynamicDataSet={getDynamicData()} />
        </DynamicDataSectionProvider>
    );
};

export default DynamicGridWrapper;
